import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Button, Select, Avatar, Empty, Breadcrumb } from "antd";
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TeamOutlined,
  PlusOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import CreateIterationDrawer from "../components/CreateIterationDrawer";
import IterationCard from "../components/IterationCard";
import useProjects from "hooks/projects/useProjects";
import useIterations from "hooks/projects/useIterations";
import { Spinner } from "react-bootstrap";
import { useTags } from "hooks";
import IterationsTableView from "../components/IterationTableView";
import { PM_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";
import { toast } from "react-toastify";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import RecentItems from "../components/RecentItems";
import ProjectDetailsDrawer from "../components/ProjectDetailsDrawer";
import IterationDetailsDrawer from "../components/IterationDetailsDrawer";
import * as pmService from "services/pmService";
import TagSearchFilter from "../components/TagSearchFilter";

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const { projects, fetchTasksByProject, loading } = useProjects();
  const {
    fetchIterationsByProject,
    createIteration,
    updateIteration,
    deleteIteration,
  } = useIterations();
  const { tags, loading: tagsLoading } = useTags();

  const [iterations, setIterations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [iterationsLoading, setIterationsLoading] = useState(false);
  const [actualPoints, setActualPoints] = useState(null);
  const [activeTab, setActiveTab] = useState("kanban");
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortMode, setSortMode] = useState("active");
  const [search, setSearch] = useState(""); // search state
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [selectedIteration, setSelectedIteration] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [iterationToDelete, setIterationToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isProjectDrawerOpen, setIsProjectDrawerOpen] = useState(false);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);

  const project = useMemo(() => {
    if (!projects || projects.length === 0) return null;
    return projects.find((p) => String(p.id) === String(projectId));
  }, [projects, projectId]);

  useEffect(() => {
    if (!projectId) return;

    const loadIterations = async () => {
      try {
        setIterationsLoading(true);
        const iterationsData = await fetchIterationsByProject(projectId);
        setIterations(iterationsData || []);

        // Fetch actual points
        pmService
          .getProjectPoints(projectId)
          .then((pts) => setActualPoints(pts))
          .catch(() => setActualPoints(null));
      } catch (err) {
        console.error("Failed to fetch iterations", err);
        setIterations([]);
      } finally {
        setIterationsLoading(false);
      }
    };

    loadIterations();
  }, [projectId, fetchIterationsByProject]);

  const getStatus = (iteration) => {
    const today = dayjs();
    const start = dayjs(iteration.startDate);
    const end = dayjs(iteration.endDate);

    if (today.isBefore(start)) return "UPCOMING";
    if (today.isAfter(end)) return "COMPLETED";
    return "ACTIVE";
  };

  const getProgress = (iteration) => {
    const today = dayjs();
    const start = dayjs(iteration.startDate);
    const end = dayjs(iteration.endDate);

    if (today.isBefore(start)) return 0;
    if (today.isAfter(end)) return 100;

    const total = end.diff(start, "day") + 1;
    const done = today.diff(start, "day") + 1;
    return Math.round((done / total) * 100);
  };

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const filteredIterations = useMemo(() => {
    let list = [...iterations];

    // if (selectedTags.length > 0) {
    //   list = list.filter((it) =>
    //     selectedTags.every((tagId) => it.tags.some((t) => t.name === tagId)),
    //   );
    // }

    if (selectedTags.length > 0) {
      list = list.filter((it) =>
        it.tags.some((t) => selectedTags.includes(t.name)),
      );
    }

    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      list = list.filter((it) => it.name.toLowerCase().includes(term));
    }

    if (sortMode === "date") {
      list.sort(
        (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
      );
    }

    if (sortMode === "active") {
      const order = { ACTIVE: 0, UPCOMING: 1, COMPLETED: 2 };
      list.sort((a, b) => order[getStatus(a)] - order[getStatus(b)]);
    }

    return list;
  }, [iterations, debouncedSearch, project, selectedTags, sortMode]);

  const handleIterationTagsUpdate = (iterationId, updatedTags) => {
    setIterations((prev) =>
      prev.map((iteration) =>
        iteration.id === iterationId
          ? { ...iteration, tags: updatedTags }
          : iteration,
      ),
    );
  };

  const handleViewIteration = (iteration) => {
    setSelectedIteration(iteration);
    setIsDetailsDrawerOpen(true);
  };

  const handleEditIteration = (iteration) => {
    setMode("edit");
    setSelectedIteration(iteration);
    setIsDrawerOpen(true);
  };

  const handleOpenIteration = (iteration) => {
    navigate(`/projects/${project.id}/iterations/${iteration.id}`);
  };

  const runOptimisticUpdate = async ({
    optimisticUpdate,
    mutationFn,
    successMessage,
    errorMessage,
  }) => {
    const previousState = iterations;

    // apply optimistic UI update
    setIterations(optimisticUpdate(previousState));

    try {
      await mutationFn();
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      // rollback
      setIterations(previousState);
      if (errorMessage) toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleSubmitProject = async (values) => {
    setDrawerLoading(true);

    const tagIds = values.tags || [];
    const fullTags = tags.filter((t) => tagIds.includes(t.id));

    try {
      if (mode === "edit") {
        await runOptimisticUpdate({
          optimisticUpdate: (prev) =>
            prev.map((it) =>
              it.id === selectedIteration.id
                ? { ...it, ...values, tags: fullTags }
                : it,
            ),

          mutationFn: async () => {
            await updateIteration(projectId, selectedIteration.id, values);

            await apiClient.post(
              `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/iterations/${selectedIteration.id}/tags`,
              tagIds,
            );
          },

          successMessage: "Iteration updated successfully",
          errorMessage: "Failed to update iteration",
        });
      } else {
        const tempId = Date.now();

        const optimisticIteration = {
          id: tempId,
          ...values,
          tags: fullTags,
        };

        await runOptimisticUpdate({
          optimisticUpdate: (prev) => [optimisticIteration, ...prev],

          mutationFn: async () => {
            const created = await createIteration(projectId, values);

            await apiClient.post(
              `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/iterations/${created.id}/tags`,
              tagIds,
            );

            // replace temp item with real backend item
            setIterations((prev) =>
              prev.map((it) =>
                it.id === tempId ? { ...created, tags: fullTags } : it,
              ),
            );
          },

          successMessage: "Iteration created successfully",
          errorMessage: "Failed to create iteration",
        });
      }
    } finally {
      setDrawerLoading(false);
      setIsDrawerOpen(false);
      setSelectedIteration(null);
    }
  };

  const handleDeleteIteration = async () => {
    if (!iterationToDelete) return;

    setDeleteLoading(true);

    const deletedItem = iterationToDelete;

    try {
      await runOptimisticUpdate({
        optimisticUpdate: (prev) =>
          prev.filter((it) => it.id !== deletedItem.id),

        mutationFn: async () => {
          await deleteIteration(projectId, deletedItem.id);
        },

        successMessage: "Iteration deleted successfully",
        errorMessage: "Failed to delete iteration",
      });
    } finally {
      setDeleteLoading(false);
      setIterationToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  if (loading || iterationsLoading || tagsLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <div className="project-page">
      {/* Header */}
      <div className="project-header">
        <div className="project-title">
          <div className="project-icon">{project?.name.charAt(0)}</div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 18 }}>{project?.name}</h1>

              <EyeOutlined
                style={{
                  fontSize: 16,
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "0.2s",
                }}
                onClick={() => setIsProjectDrawerOpen(true)}
                onMouseEnter={(e) => (e.target.style.color = "#111827")}
                onMouseLeave={(e) => (e.target.style.color = "#6b7280")}
              />
            </div>
            <Breadcrumb
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
              items={[
                {
                  title: (
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#6b7280",
                        fontFamily: "Inter",
                      }}
                      onClick={() => navigate("/general/projects")}
                    >
                      Projects
                    </span>
                  ),
                },
                {
                  title: (
                    <span
                      style={{
                        color: "#111827",
                        fontWeight: 500,
                        fontFamily: "Inter",
                      }}
                    >
                      {project?.name}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <RecentItems />
          <Avatar.Group max={3}>
            <Avatar>{project?.ownerName.charAt(0)}</Avatar>
          </Avatar.Group>
        </div>
      </div>

      {/* Meta / Stats */}
      <div className="project-meta" style={{ marginBottom: "24px" }}>
        <div className="meta-item">
          <div className="meta-icon meta-blue">
            <CalendarOutlined />
          </div>
          <span>{iterations?.length} Iterations</span>
        </div>
        <div className="meta-item">
          <div className="meta-icon meta-purple">
            <FileTextOutlined />
          </div>
          <span>{project?.taskCount || 0} Tasks</span>
        </div>
        <div className="meta-item">
          <div className="meta-icon meta-green">
            <TeamOutlined />
          </div>
          <span>Target: {project?.targetPoints ?? 0} pts</span>
        </div>
        {actualPoints !== null && (
          <div className="meta-item">
            <div
              className="meta-icon"
              style={{ background: "#e8f5e9", color: "#2e7d32" }}
            >
              <FileTextOutlined />
            </div>
            <span>Actual: {actualPoints} pts</span>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="project-toolbar">
        <div className="custom-tabs">
          <div
            className={`tab-item ${activeTab === "kanban" ? "active" : ""}`}
            onClick={() => setActiveTab("kanban")}
          >
            <AppstoreOutlined />
            Kanban
          </div>

          <div
            className={`tab-item ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
          >
            <UnorderedListOutlined />
            List
          </div>

          {/* <div
            className={`tab-item ${activeTab === "table" ? "active" : ""}`}
            onClick={() => setActiveTab("table")}
          >
            <TableOutlined /> Table
          </div> */}
          {/* <div
            className={`tab-item ${activeTab === "timeline" ? "active" : ""}`}
            onClick={() => setActiveTab("timeline")}
          >
            <ClockCircleOutlined /> Timeline
          </div> */}
        </div>

        <div className="toolbar-actions">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />

          {/* <Select
            mode="multiple"
            placeholder="Filter by tag"
            style={{ width: 200 }}
            value={selectedTags}
            onChange={setSelectedTags}
            options={tags.map((t) => ({
              value: t.name,
              label: t.name,
            }))}
            maxTagCount={1}
          /> */}
          <TagSearchFilter
            tags={tags}
            value={selectedTags}
            onChange={setSelectedTags}
            loading={tagsLoading}
          />

          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="new-task-btn"
            onClick={() => {
              setMode("create");
              setSelectedIteration(null);
              setIsDrawerOpen(true);
            }}
          >
            New Iteration
          </Button>
        </div>
      </div>

      {selectedTags.length > 0 && (
        <div className="active-filter">
          Filtering by tag:
          {selectedTags.map((tag) => (
            <span key={tag} className="active-filter-tag">
              {tag}
              <span
                className="remove-filter"
                onClick={() =>
                  setSelectedTags((prev) => prev.filter((t) => t !== tag))
                }
              >
                ✕
              </span>
            </span>
          ))}
          <span className="clear-all" onClick={() => setSelectedTags([])}>
            Clear all
          </span>
        </div>
      )}

      {/* Iterations */}
      {activeTab === "kanban" ? (
        filteredIterations.length > 0 ? (
          <div className="projects-grid">
            {filteredIterations.map((iteration, index) => (
              <IterationCard
                index={index}
                key={iteration.id}
                projectId={projectId}
                iteration={iteration}
                status={getStatus(iteration)}
                progress={getProgress(iteration)}
                view={activeTab}
                onOpen={handleOpenIteration}
                selectedTags={selectedTags}
                onTagClick={(tag) =>
                  setSelectedTags((prev) =>
                    prev.includes(tag)
                      ? prev.filter((t) => t !== tag)
                      : [...prev, tag],
                  )
                }
                onView={() => handleViewIteration(iteration)}
                onEdit={() => handleEditIteration(iteration)}
                onDelete={() => {
                  setIterationToDelete(iteration);
                  setDeleteModalOpen(true);
                }}
              />
            ))}
          </div>
        ) : (
          <div style={{ paddingTop: 40, textAlign: "center" }}>
            <Empty
              description={
                selectedTags.length > 0 && search
                  ? "No iterations match your search and selected tags"
                  : selectedTags.length > 0
                  ? "No iterations match your selected tags"
                  : search
                  ? "No iterations match your search"
                  : "No iterations found"
              }
            />
          </div>
        )
      ) : activeTab === "list" ? (
        filteredIterations.length > 0 ? (
          <IterationsTableView
            iterations={filteredIterations}
            projectId={projectId}
            openEditDrawer={handleEditIteration}
            onDelete={(iteration) => {
              setIterationToDelete(iteration);
              setDeleteModalOpen(true);
            }}
          />
        ) : (
          <div style={{ paddingTop: 40, textAlign: "center" }}>
            <Empty
              description={
                selectedTags.length > 0 && search
                  ? "No iterations match your search and selected tags"
                  : selectedTags.length > 0
                  ? "No iterations match your selected tags"
                  : search
                  ? "No iterations match your search"
                  : "No iterations found"
              }
            />
          </div>
        )
      ) : null}

      <ProjectDetailsDrawer
        visible={isProjectDrawerOpen}
        onClose={() => setIsProjectDrawerOpen(false)}
        project={project}
        onEditProject={() => {}}
      />

      <IterationDetailsDrawer
        open={isDetailsDrawerOpen}
        onClose={() => setIsDetailsDrawerOpen(false)}
        iteration={selectedIteration}
        project={project}
        tasks={tasks}
        source="project-details"
        onTagsUpdate={handleIterationTagsUpdate}
      />

      <CreateIterationDrawer
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedIteration(null);
        }}
        projects={projects}
        availableTags={tags}
        mode={mode}
        initialValues={selectedIteration}
        onSubmit={handleSubmitProject}
        loading={drawerLoading}
        onTagsUpdate={handleIterationTagsUpdate}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        setShow={setDeleteModalOpen}
        loading={deleteLoading}
        itemName="Iteration"
        message={
          iterationToDelete
            ? `Are you sure you want to delete "${iterationToDelete.name}" iteration? This action cannot be undone.`
            : undefined
        }
        onConfirm={handleDeleteIteration}
      />
    </div>
  );
};

export default ProjectDetails;
